import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { CalendarDays, Check, Circle, Gift, Loader2, PlayCircle, Plus, Upload } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/components/ui/use-toast'
import { fetchCurrentUser } from '@/redux/userSlice'
import type { AppDispatch } from '@/store'
import { brandsApi } from '@/lib/api/brands'
import { brandSetupItems, brandSetupScore, lookingForOf } from '@/lib/brandProfile'
import { cn } from '@/lib/utils'
import { useGetBrandCatalogQuery, useUpdateOrganizationMutation } from '@/redux/marketerApi'
import { SettingsCard } from './SettingsCard'

const DEALS = [
  { id: 'gifting', label: 'Gifting', icon: Gift },
  { id: 'appearance', label: 'Appearance', icon: CalendarDays },
  { id: 'licensing', label: 'Content Licensing', icon: PlayCircle },
] as const

const field =
  'h-11 w-full rounded-xl border border-border bg-card px-3 text-base text-foreground outline-none focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40'

const Mark: React.FC<{ src: string | null | undefined; name: string }> = ({ src, name }) =>
  src ? (
    <img src={src} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
  ) : (
    <span
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#F97316] text-lg font-bold"
      style={{ color: '#fff' }}
    >
      {name.slice(0, 2).toUpperCase()}
    </span>
  )

/** Uploads an image and returns its public URL */
async function uploadImage(file: File) {
  const ext = file.type.split('/')[1] || 'png'
  const name = `logo-${Date.now()}.${ext}`
  const { uploadUrl, publicUrl } = (await brandsApi.getUploadUrl(name, file.type)).data
  await brandsApi.uploadFile(uploadUrl, file)
  return publicUrl
}

/**
 * Settings > Brand Profile: the company, its main brand, what it's about and the deals it runs,
 * with how complete the profile is. Sports, regions and pricing are edited in the full setup.
 */
export const BrandProfileTab: React.FC<{ onAddBrand: () => void }> = ({ onAddBrand }) => {
  const { toast } = useToast()
  const dispatch = useDispatch<AppDispatch>()
  const { marketer } = useAuth()
  const org = (marketer as any)?.org
  const lf = lookingForOf(org)
  const { data: catalog, refetch } = useGetBrandCatalogQuery()
  const brand = catalog?.brands[0]
  const [save, { isLoading: saving }] = useUpdateOrganizationMutation()
  const [name, setName] = useState(org?.name ?? '')
  const [about, setAbout] = useState(lf.about ?? '')
  const [deals, setDeals] = useState<string[]>(lf.deal_types)
  const [uploading, setUploading] = useState<'company' | 'brand' | null>(null)
  const companyLogo = useRef<HTMLInputElement>(null)
  const brandLogo = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setName(org?.name ?? '')
    setAbout(lookingForOf(org).about ?? '')
    setDeals(lookingForOf(org).deal_types)
  }, [org?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const items = brandSetupItems(org)
  const score = brandSetupScore(org)
  const dirty =
    name !== (org?.name ?? '') ||
    about !== (lf.about ?? '') ||
    deals.join() !== lf.deal_types.join()

  const persist = async (patch: Parameters<typeof save>[0], message = 'Saved') => {
    try {
      await save(patch).unwrap()
      await dispatch(fetchCurrentUser())
      toast({ title: message })
    } catch (err: any) {
      toast({
        title: "Couldn't save",
        description: err?.data?.error ?? 'Try again.',
        variant: 'destructive',
      })
    }
  }

  const changeLogo = async (which: 'company' | 'brand', file: File) => {
    setUploading(which)
    try {
      const url = await uploadImage(file)
      if (which === 'company') await persist({ logo: url }, 'Company logo updated')
      else if (brand) {
        await brandsApi.updateBrand(brand.id, { name: brand.name, logo: url })
        await refetch()
        toast({ title: 'Brand logo updated' })
      }
    } catch {
      toast({ title: "Couldn't upload the logo", variant: 'destructive' })
    } finally {
      setUploading(null)
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
      <div className="min-w-0 space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <SettingsCard
            title="Your company"
            subtitle="Your company name is only visible to your team."
          >
            <div className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground">
                  Company name
                </span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  className={field}
                />
              </label>
              <div>
                <span className="mb-1.5 block text-sm font-medium text-foreground">
                  Company logo
                </span>
                <div className="flex items-center gap-2">
                  <Mark src={org?.logo} name={name || 'Co'} />
                  <button
                    type="button"
                    onClick={() => companyLogo.current?.click()}
                    disabled={!!uploading}
                    className="h-10 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
                  >
                    {uploading === 'company' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Change logo'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </SettingsCard>

          <SettingsCard
            title="Your brand"
            subtitle="The brand name and logo athletes see. You can add more than one."
          >
            {brand ? (
              <div className="flex flex-wrap items-center gap-3">
                <Mark src={brand.logo} name={brand.name} />
                <span className="min-w-0 flex-1 truncate text-lg font-bold text-foreground">
                  {brand.name}
                </span>
                <button
                  type="button"
                  onClick={() => brandLogo.current?.click()}
                  disabled={!!uploading}
                  className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
                >
                  {uploading === 'brand' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Change logo
                </button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No brand yet.</p>
            )}
            <button
              type="button"
              onClick={onAddBrand}
              className="mt-3 inline-flex h-10 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
            >
              <Plus className="h-4 w-4" /> Add another brand
            </button>
          </SettingsCard>
        </div>

        <SettingsCard
          title="What your brand is about"
          subtitle="One or two sentences. Athletes read this to understand what you do."
        >
          <textarea
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            maxLength={300}
            rows={3}
            placeholder="We run athlete campaigns for sports nutrition and training brands in the Midwest."
            className="w-full resize-none rounded-xl border border-border bg-card px-3 py-2.5 text-base text-foreground outline-none focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40"
          />
          <p className="mt-1 text-right text-xs tabular-nums text-muted-foreground">
            {about.length}/300
          </p>
        </SettingsCard>

        <SettingsCard
          title="The deals you run"
          subtitle="Pick everything you'd do. You can mix them per campaign."
        >
          <div className="flex flex-wrap gap-2">
            {DEALS.map((d) => {
              const on = deals.includes(d.id)
              return (
                <button
                  key={d.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    setDeals((x) => (on ? x.filter((v) => v !== d.id) : [...x, d.id]))
                  }
                  className={cn(
                    'inline-flex h-11 items-center gap-2 rounded-full border px-5 text-base font-semibold transition-colors',
                    on
                      ? 'border-[#9BD85A] bg-[#C0FD72] text-[#0F172A]'
                      : 'border-border bg-card text-foreground hover:border-[#9BD85A]',
                  )}
                >
                  <d.icon className="h-4 w-4" /> {d.label}
                </button>
              )
            })}
          </div>
        </SettingsCard>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={!dirty || saving || !name.trim()}
            onClick={() =>
              persist(
                {
                  name: name.trim(),
                  looking_for: { ...lf, about: about.trim() || null, deal_types: deals },
                },
                'Brand profile saved',
              )
            }
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-[#C0FD72] px-6 font-semibold text-[#0F172A] disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save changes
          </button>
        </div>
      </div>

      <SettingsCard title="Profile completeness" className="h-fit">
        <div className="mb-1 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Complete</span>
          <span className="font-bold tabular-nums text-foreground">{score}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-[#84CC16]" style={{ width: `${score}%` }} />
        </div>
        <ul className="mt-4 space-y-2.5">
          {items.map((i) => (
            <li key={i.id} className="flex items-center gap-2.5 text-sm text-foreground">
              {i.done ? (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#16A34A]">
                  <Check className="h-3 w-3 text-[#FFFFFF]" strokeWidth={3} />
                </span>
              ) : (
                <Circle className="h-5 w-5 text-[#CBD5E1]" />
              )}
              {i.label}
            </li>
          ))}
        </ul>
        <Link
          to="/setup"
          className="mt-4 inline-block text-sm font-semibold text-[#3F7A0A] hover:underline"
        >
          Edit sports, regions and pricing →
        </Link>
        <input
          ref={companyLogo}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) changeLogo('company', f)
            e.target.value = ''
          }}
        />
        <input
          ref={brandLogo}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) changeLogo('brand', f)
            e.target.value = ''
          }}
        />
      </SettingsCard>
    </div>
  )
}

export default BrandProfileTab
